/**
 * Portrait Card - Tall vertical card with immersive overlay
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { ExploreEntry } from '../../Model/Explore/ExploreEntry';
import { Recipe } from '../../Model/Diet/Recipe/Recipe';
import { useEntryText } from './useEntryText';
import { getContentTypeLabel, getContentTypeIcon } from './contentTypeUtils';

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  max-height: 400px;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .image {
      transform: scale(1.08);
    }

    .gradient-border {
      opacity: 1;
    }

    .content {
      transform: translateY(-8px);
    }

    .category-pill {
      background: #00dda2;
      color: white;
    }
  }
`;

const GradientBorder = styled.div`
  position: absolute;
  inset: -2px;
  background: #00dda2;
  border-radius: 26px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
`;

const Inner = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  overflow: hidden;
  isolation: isolate;
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: -1px;
  background: ${({ image }) => (image ? `url('${image}')` : 'linear-gradient(180deg, #2E2E2E 0%, #26323A 100%)')};
  background-size: cover;
  background-position: center center;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    transparent 40%,
    rgba(0, 0, 0, 0.4) 60%,
    rgba(0, 0, 0, 0.95) 100%
  );
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  color: white;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CategoryPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  transition: all 0.3s ease;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: white;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  font-size: 12px;
  opacity: 0.8;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export type PortraitCardProps = {
  entry: ExploreEntry | Recipe;
  linkPrefix?: string;
};

export const PortraitCard: React.FC<PortraitCardProps> = observer(({ entry, linkPrefix = '/academy/' }) => {
  const { title, type, duration, imageLarge, imageMedium } = useEntryText(entry);
  const navigate = useNavigate();

  const backgroundImage = imageLarge || imageMedium;

  const handleClick = React.useCallback(() => {
    navigate(`${linkPrefix}${entry.id}`);
  }, [navigate, linkPrefix, entry]);

  return (
    <CardContainer onClick={handleClick}>
      <GradientBorder className="gradient-border" />
      <Inner>
        <BackgroundImage className="image" image={backgroundImage} />
        <Overlay />
        <Content className="content">
          <CategoryPill className="category-pill">
            {getContentTypeIcon(type)} {getContentTypeLabel(type)}
          </CategoryPill>
          <Title>{title}</Title>
          {duration && duration > 0 && (
            <MetaRow>
              <MetaItem>⏱ {duration} min</MetaItem>
            </MetaRow>
          )}
        </Content>
      </Inner>
    </CardContainer>
  );
});
